import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { navigateWithFrom } from '../utils/navigationUtils';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString } from '../utils/security';
import { 
  fetchServiceRecordDetail,
  fetchAppointmentsByServiceRecord,
  createAppointment,
  updateAppointment,
  cancelAppointment
} from '../services/serviceRecordsService';
import { fetchSiparisler, fetchSiparisDetay } from '../services/siparisService';
import { fetchIadeKargolari } from '../services/iadeKargoService';
import { fetchTechServices } from '../services/techServicesService';
import { fetchCities, fetchDistrictsByCity } from '../services/locationsService';
import AppointmentWizardModal from '../components/AppointmentWizardModal';
import SiparisDetayModal from '../components/SiparisDetayModal';
import TechServiceAssignmentHistory from '../components/TechServiceAssignmentHistory';
import TeknikServisDegistirModal from '../components/TeknikServisDegistirModal';
import ServisKaydiCihazDuzenleModal from '../components/ServisKaydiCihazDuzenleModal';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import './ServiceRecordUpdatePage.css';
import { FieldLabel } from '../components/FieldLabel';
import { getOnemBorderClass, getOnemRowClass, getPriorityToken } from '../utils/onemDerecesiHelper';

const IADE_KARGO_DURUM_LABEL = {
  KargoBekliyor: 'Kargo Bekliyor',
  Kargoda: 'Kargoda',
  Tamamlandi: 'Tamamlandı',
};

// Durum sabitleri - dokümantasyona göre
const SERVICE_RECORD_STATUSES = {
  RANDEVU_BEKLENIYOR: 'Randevu Bekleniyor',
  SIPARIS_BEKLENIYOR: 'Sipariş Bekleniyor',
  TEKNIK_SERVIS_ATAMASI_BEKLIYOR: 'Teknik Servis Ataması Bekliyor',
  TEKNISYEN_ATAMASI_BEKLIYOR: 'Teknisyen Ataması Bekliyor',
  RANDEVU_TARIHI_BEKLENIYOR: 'Randevu Tarihi Bekleniyor',
  TAMAMLANDI: 'Tamamlandı',
};

// Geçerli randevu durumları (Randevu Bekleniyor servis kaydı durumudur, randevu durumu değil)
const APPOINTMENT_STATUSES = {
  RANDEVU_TARIHI_BEKLENIYOR: 'Randevu Tarihi Bekleniyor',
  ISLEMDE: 'İşlemde',
  RANDEVU_TAMAMLANDI: 'Randevu Tamamlandı',
  RANDEVU_IPTAL_EDILDI: 'Randevu İptal Edildi',
};

const TIME_SLOTS = [
  { value: '09:00-11:00', label: '09:00 - 11:00' },
  { value: '11:00-13:00', label: '11:00 - 13:00' },
  { value: '13:00-15:00', label: '13:00 - 15:00' },
  { value: '15:00-17:00', label: '15:00 - 17:00' },
  { value: '17:00-19:00', label: '17:00 - 19:00' },
];

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const getDeviceName = (device = {}) => {
  return (
    device.urun?.icerikAdi ||
    device.urun?.urunAdi ||
    device.icerik?.icerikAdi ||
    device.icerik?.urunAdi ||
    device.urunAdi ||
    device.icerikAdi ||
    device.cihazAdi ||
    device.ad ||
    '---'
  );
};

const getDeviceSerial = (device = {}) => {
  return device.seriNumarasi || device.seriNo || device.serial || '---';
};

const ServiceRecordUpdatePage = () => {
  const { confirm, promptText } = useConfirmDialog();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const userRol = (user?.rol || '').toLowerCase();
  const isTech = ['tech-service', 'tech-person'].includes(userRol);
  const isSales = userRol === 'sales';
  const isAdminOrMod = userRol === 'admin' || userRol === 'mod' || userRol === 'moderator';
  const goBack = useBackRoute(isTech ? '/tech-service/service-records' : '/service-records');
  
  // Erişim: Admin, Mod, Müşteri Hizmetleri (sales), Teknik Servis
  useEffect(() => {
    if (!authLoading && user) {
      const isAuthorized =
        user.rol === 'admin' ||
        user.rol === 'moderator' ||
        user.rol === 'mod' ||
        user.rol === 'sales' ||
        user.rol === 'tech-service';
      if (!isAuthorized) {
        navigate('/', { replace: true });
      }
    }
  }, [user, authLoading, navigate]);
  
  // Servis kaydı bilgileri
  const [serviceRecord, setServiceRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Randevular
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  
  // Yeni randevu formu
  const [isNewAppointmentModalOpen, setIsNewAppointmentModalOpen] = useState(false);
  const [newAppointmentForm, setNewAppointmentForm] = useState({
    randevuTarihi: '',
    randevuSaatAraligi: '',
    teknikServisId: '',
    aciklama: '',
  });
  const [newAppointmentSubmitting, setNewAppointmentSubmitting] = useState(false);
  const [newAppointmentError, setNewAppointmentError] = useState('');
  
  // Teknik servis seçimi
  const [techServices, setTechServices] = useState([]);
  const [techServicesLoading, setTechServicesLoading] = useState(false);
  const [techServiceCityId, setTechServiceCityId] = useState('');
  const [techServiceDistrictId, setTechServiceDistrictId] = useState('');
  const [techServiceIlceler, setTechServiceIlceler] = useState([]);
  const [iller, setIller] = useState([]);
  const [techServicePage, setTechServicePage] = useState(1);
  const [techServicePagination, setTechServicePagination] = useState({ total: 0, totalPages: 1 });
  const [showTechServiceSelection, setShowTechServiceSelection] = useState(false);
  const [autoSelectedTechService, setAutoSelectedTechService] = useState(null);
  
  // Teknisyen atama modal
  const [isTechnicianModalOpen, setIsTechnicianModalOpen] = useState(false);
  const [selectedAppointmentForTechnician, setSelectedAppointmentForTechnician] = useState(null);
  const [technicianForm, setTechnicianForm] = useState({
    teknisyenId: '',
    adi: '',
    soyadi: '',
    telefon: '',
  });
  
  // İşlem ekleme modal
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);
  const [selectedAppointmentForOperation, setSelectedAppointmentForOperation] = useState(null);
  const [operationForm, setOperationForm] = useState({
    kategoriId: '',
    islemAdi: '',
    aciklama: '',
  });
  
  // Parça ekleme modal
  const [isPartModalOpen, setIsPartModalOpen] = useState(false);
  const [selectedAppointmentForPart, setSelectedAppointmentForPart] = useState(null);
  const [partForm, setPartForm] = useState({
    parcaAdi: '',
    parcaKodu: '',
    adet: 1,
    fiyat: '',
    aciklama: '',
  });
  
  // Randevu tamamlama modal
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [selectedAppointmentForFinish, setSelectedAppointmentForFinish] = useState(null);
  const [finishForm, setFinishForm] = useState({
    garantiDahilinde: false,
    musteriyeFaturaEdilen: '',
  });

  // Sekme
  const [activeTab, setActiveTab] = useState('record');

  // Siparişler ve İade Kargoları (servis kaydı randevularına ait)
  const [siparisler, setSiparisler] = useState([]);
  const [iadeKargolari, setIadeKargolari] = useState([]);
  const [siparislerLoading, setSiparislerLoading] = useState(false);
  const [iadeKargolariLoading, setIadeKargolariLoading] = useState(false);
  const [linkedSiparisModal, setLinkedSiparisModal] = useState(null);
  const [tsDegistirOpen, setTsDegistirOpen] = useState(false);
  const [cihazDuzenleOpen, setCihazDuzenleOpen] = useState(false);

  // Servis kaydı detayını yükle
  useEffect(() => {
    const loadServiceRecord = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetchServiceRecordDetail(id);
        const record = response?.veri?.servisKaydi || response?.veri || null;
        
        if (record) {
          setServiceRecord(record);
          
          // Adres bilgilerine göre teknik servisleri yükle
          if (record.adres && record.musteri) {
            const customer = record.musteri;
            const cityId = customer.il?.id || customer.cityId;
            const districtId = customer.ilce?.id || customer.districtId;
            
            if (cityId) {
              loadTechServicesByLocation(cityId, districtId);
            }
          }
        } else {
          throw new Error('Servis kaydı bulunamadı');
        }
      } catch (error) {
        console.error('Servis kaydı yüklenemedi:', error);
        setError(getErrorMessage(error, 'Servis kaydı yüklenemedi'));
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(error, 'Servis kaydı yüklenemedi'));
        }
        navigate('/service-records');
      } finally {
        setLoading(false);
      }
    };
    
    loadServiceRecord();
  }, [id, navigate]);

  // Siparişler ve İade Kargolarını servis kaydı ID'sine göre yükle
  useEffect(() => {
    if (!id) {
      setSiparisler([]);
      setIadeKargolari([]);
      return;
    }
    const load = async () => {
      setSiparislerLoading(true);
      setIadeKargolariLoading(true);
      try {
        const [sipRes, ikRes] = await Promise.all([
          fetchSiparisler({ servisKaydiId: id, limit: 100 }),
          fetchIadeKargolari({ servisKaydiId: id, limit: 100 }),
        ]);
        setSiparisler(sipRes?.veri?.siparisler || []);
        setIadeKargolari(ikRes?.veri?.iadeKargolari || []);
      } catch {
        setSiparisler([]);
        setIadeKargolari([]);
      } finally {
        setSiparislerLoading(false);
        setIadeKargolariLoading(false);
      }
    };
    load();
  }, [id]);

  // Randevuları yükle
  const loadAppointments = useCallback(async () => {
    if (!id) return;
    
    setAppointmentsLoading(true);
    try {
      console.log('Randevular yükleniyor, servis kaydı ID:', id);
      // Aktif randevuları getir, ama durum filtresi ile iptal edilenleri de göster
      const response = await fetchAppointmentsByServiceRecord(id, { aktif: true });
      console.log('Randevular response:', response);
      const list = response?.veri?.randevular || response?.veri?.randevular || response?.veri || [];
      console.log('Randevular listesi:', list);
      setAppointments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Randevular yüklenemedi:', err);
      setAppointments([]);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [id]);

  const reloadServiceRecord = useCallback(async () => {
    if (!id) return;
    try {
      const response = await fetchServiceRecordDetail(id);
      const record = response?.veri?.servisKaydi || response?.veri || null;
      if (record) setServiceRecord(record);
      await loadAppointments();
    } catch (err) {
      console.error('Servis kaydı yenilenemedi:', err);
    }
  }, [id, loadAppointments]);

  // Sayfa yüklendiğinde randevuları yükle
  useEffect(() => {
    if (id) {
      loadAppointments();
    }
  }, [id, loadAppointments]);

  // Randevu sekmesine tıklanınca randevuları yükle
  useEffect(() => {
    if (activeTab === 'appointments' && id) {
      console.log('Randevu sekmesi açıldı, randevular yükleniyor...');
      loadAppointments();
    }
  }, [activeTab, id, loadAppointments]);

  // İlleri yükle
  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setIller([]);
      }
    };
    loadCities();
  }, []);

  // İlçeleri yükle (teknik servis seçimi için)
  useEffect(() => {
    const loadDistricts = async () => {
      if (!techServiceCityId || techServiceCityId.trim() === '') {
        setTechServiceIlceler([]);
        return;
      }
      
      const cityIdNum = parseInt(techServiceCityId, 10);
      if (isNaN(cityIdNum)) {
        setTechServiceIlceler([]);
        return;
      }
      
      try {
        const response = await fetchDistrictsByCity(cityIdNum);
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri || [];
        if (!Array.isArray(list)) {
          list = [];
        }
        
        // API formatı: { id: number, adi: string, cityId: number }
        // Direkt kullan, normalize etmeye gerek yok
        const serializedList = list.map((ilce) => {
          // Eğer Mongoose document ise (_doc varsa), _doc içindeki veriyi kullan
          if (ilce._doc) {
            return {
              id: ilce._doc._id || ilce._doc.id,
              adi: ilce._doc.adi || ilce._doc.ad || ilce._doc.name,
              ad: ilce._doc.adi || ilce._doc.ad || ilce._doc.name, // Geriye dönük uyumluluk için
              name: ilce._doc.name || ilce._doc.adi || ilce._doc.ad, // Geriye dönük uyumluluk için
              cityId: ilce._doc.cityId
            };
          }
          // API'den direkt { id, adi, cityId } formatında geliyor
          return {
            id: ilce._id || ilce.id,
            adi: ilce.adi || ilce.ad || ilce.name,
            ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
            name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
            cityId: ilce.cityId
          };
        });
        
        setTechServiceIlceler(serializedList);
      } catch (err) {
        console.error('İlçeler yüklenemedi:', err);
        setTechServiceIlceler([]);
      }
    };
    
    loadDistricts();
  }, [techServiceCityId]);

  // Teknik servisleri yükle
  const loadTechServicesByLocation = useCallback(async (cityId, districtId, page = 1) => {
    if (!cityId) return [];
    
    setTechServicesLoading(true);
    try {
      const response = await fetchTechServices({
        cityId,
        districtId: districtId || undefined,
        aktif: true,
        page,
        limit: 100, // Tüm servisleri getirmek için limit artırıldı
      });
      
      const list = response?.veri?.teknikServisler || response?.veri || [];
      const services = Array.isArray(list) ? list : [];
      setTechServices(services);
      
      const pagination = response?.veri?.sayfalama || {};
      setTechServicePagination({
        total: pagination.toplam || 0,
        totalPages: pagination.toplamSayfa || 1,
      });
      setTechServicePage(page);
      
      return services;
    } catch (err) {
      console.error('Teknik servisler yüklenemedi:', err);
      setTechServices([]);
      return [];
    } finally {
      setTechServicesLoading(false);
    }
  }, []);

  // İl değiştiğinde teknik servisleri yükle (manuel seçim modunda)
  useEffect(() => {
    if (techServiceCityId && showTechServiceSelection) {
      loadTechServicesByLocation(
        parseInt(techServiceCityId, 10),
        techServiceDistrictId ? parseInt(techServiceDistrictId, 10) : undefined,
        1
      );
      // İl/ilçe değiştiğinde otomatik seçimi temizle
      setAutoSelectedTechService(null);
      setNewAppointmentForm(prev => ({ ...prev, teknikServisId: '' }));
    }
  }, [techServiceCityId, techServiceDistrictId, showTechServiceSelection, loadTechServicesByLocation]);

  // Modal açıldığında otomatik teknik servis seçimi
  useEffect(() => {
    if (isNewAppointmentModalOpen) {
      // Tech-service kullanıcıları için kendi teknik servislerini kullan
      if (user?.rol === 'tech-service') {
        const techServiceId = user?.techService || user?.techServiceId;
        if (techServiceId) {
          // Tech-service kullanıcısının teknik servisini bul ve set et
          // Eğer serviceRecord'da teknik servis bilgisi varsa onu kullan, yoksa user'dan al
          const techService = serviceRecord?.teknikServis || {
            id: techServiceId,
            _id: techServiceId,
            ad: user?.techServiceName || 'Teknik Servis',
            name: user?.techServiceName || 'Teknik Servis'
          };
          setAutoSelectedTechService(techService);
          setShowTechServiceSelection(false);
        } else {
          setAutoSelectedTechService(null);
          setShowTechServiceSelection(true);
        }
      } else if (serviceRecord?.adres) {
        // Admin/Mod kullanıcıları için adres bazlı seçim
        const address = serviceRecord.adres;
        const cityId = address.city?.id || address.cityId;
        const districtId = address.district?.id || address.districtId;
        
        if (cityId) {
          // İl ve ilçe bilgilerini set et
          setTechServiceCityId(String(cityId));
          if (districtId) {
            setTechServiceDistrictId(String(districtId));
          }
          
          // O ilçedeki teknik servisleri yükle ve otomatik seç
          loadTechServicesByLocation(
            parseInt(cityId, 10),
            districtId ? parseInt(districtId, 10) : undefined,
            1
          ).then((services) => {
            if (services && services.length > 0) {
              // İlçe bazlı filtreleme - önce ilçedeki servisleri kontrol et
              let selectedService = null;
              
              if (districtId) {
                const districtServices = services.filter(service => {
                  const serviceDistrictId = service.ilce?.id || service.districtId;
                  return serviceDistrictId && String(serviceDistrictId) === String(districtId);
                });
                
                if (districtServices.length > 0) {
                  selectedService = districtServices[0];
                } else {
                  // İlçede servis yoksa il bazlı ilk servisi seç
                  selectedService = services[0];
                }
              } else {
                // İlçe yoksa il bazlı ilk servisi seç
                selectedService = services[0];
              }
              
              if (selectedService) {
                setAutoSelectedTechService(selectedService);
                setNewAppointmentForm(prev => ({
                  ...prev,
                  teknikServisId: selectedService.id || selectedService._id
                }));
                setShowTechServiceSelection(false);
              } else {
                setAutoSelectedTechService(null);
                setShowTechServiceSelection(true);
              }
            } else {
              setAutoSelectedTechService(null);
              setShowTechServiceSelection(true);
            }
          });
        } else {
          setShowTechServiceSelection(true);
        }
      } else {
        setShowTechServiceSelection(true);
      }
    } else if (!isNewAppointmentModalOpen) {
      // Modal kapandığında state'leri sıfırla
      setShowTechServiceSelection(false);
      setAutoSelectedTechService(null);
      setTechServiceCityId('');
      setTechServiceDistrictId('');
      setTechServices([]);
      setNewAppointmentForm(prev => ({ ...prev, teknikServisId: '' }));
    }
  }, [isNewAppointmentModalOpen, serviceRecord, loadTechServicesByLocation, user]);

  // Yeni randevu oluştur (wizard'dan gelen data)
  const handleCreateAppointment = async (appointmentData) => {
    setNewAppointmentError('');
    
    // Wizard'dan gelen data validation
    if (!appointmentData.randevuTarihi) {
      setNewAppointmentError('Randevu tarihi seçilmedi');
      return;
    }
    
    if (!appointmentData.randevuSaati) {
      setNewAppointmentError('Saat seçilmedi');
      return;
    }
    
    if (!appointmentData.teknikServisId) {
      setNewAppointmentError('Teknik servis seçilmedi');
      return;
    }
    
    setNewAppointmentSubmitting(true);
    try {
      // Debug: Gelen tarih verisini logla
      console.log('Wizard\'dan gelen randevuTarihi:', appointmentData.randevuTarihi);
      console.log('randevuTarihi tipi:', typeof appointmentData.randevuTarihi);
      
      // Tarihi YYYY-MM-DD formatına çevir (timezone sorununu önlemek için)
      let dateStr;
      if (typeof appointmentData.randevuTarihi === 'string' && appointmentData.randevuTarihi.includes('-')) {
        // Eğer zaten YYYY-MM-DD formatındaysa direkt kullan
        dateStr = appointmentData.randevuTarihi.split('T')[0];
      } else {
        // Date objesinden dönüştür (timezone-safe yöntem)
        const appointmentDate = new Date(appointmentData.randevuTarihi);
        const year = appointmentDate.getFullYear();
        const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
        const day = String(appointmentDate.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      }
      
      console.log('Oluşturulan dateStr:', dateStr);
      
      // DateTime birleştir
      const appointmentDateTime = `${dateStr}T${appointmentData.randevuSaati}:00`;
      console.log('Final appointmentDateTime:', appointmentDateTime);
      
      const payload = {
        randevuTarihi: appointmentDateTime,
        randevuSaati: appointmentData.randevuSaati,
        teknikServisId: appointmentData.teknikServisId,
        aciklama: appointmentData.aciklama || undefined,
      };
      
      // Teknisyen ID'si varsa ekle
      if (appointmentData.teknisyenId) {
        payload.teknisyenId = appointmentData.teknisyenId;
      }
      
      // Cihaz bilgilerinden garanti bitiş tarihini hesapla
      if (serviceRecord?.cihazlar && serviceRecord.cihazlar.length > 0) {
        const device = serviceRecord.cihazlar[0]; // İlk cihazı al
        if (device.garantiBaslangicTarihi && device.garantiSuresi) {
          payload.garantiBaslangicTarihi = device.garantiBaslangicTarihi;
          payload.garantiSuresi = device.garantiSuresi;
        }
      }
      
      const response = await createAppointment(id, payload);
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Randevu oluşturulamadı');
      }
      
      console.log('Randevu oluşturma response:', response);
      
      // Response'dan oluşturulan randevuyu al
      const newAppointment = response?.veri?.randevu || response?.veri || null;
      console.log('Yeni oluşturulan randevu:', newAppointment);
      
      if (!newAppointment || !newAppointment._id) {
        throw new Error('Randevu oluşturuldu ancak randevu bilgisi alınamadı');
      }
      
      const randevuId = newAppointment._id || newAppointment.id;
      console.log('Randevu ID:', randevuId);
      console.log('Servis Kaydı ID:', id);
      console.log('Teknik Servis ID:', payload.teknikServisId);
      console.log('Mevcut Servis Kaydı Durumu:', serviceRecord?.durum);
      
      // Backend randevu oluşturulduğunda otomatik olarak servis kaydı durumunu güncelliyor
      // Bu yüzden frontend'den manuel güncelleme yapmaya gerek yok
      // Sadece backend'den güncel verileri yükle
      try {
        console.log('[handleCreateAppointment] Servis kaydı yeniden yükleniyor, ID:', id);
        console.log('[handleCreateAppointment] Mevcut servis kaydı durumu (randevu oluşturulmadan önce):', serviceRecord?.durum);
        
        const updatedServiceRecord = await fetchServiceRecordDetail(id);
        console.log('[handleCreateAppointment] Backend\'den gelen servis kaydı response:', updatedServiceRecord);
        
        if (updatedServiceRecord?.basarili !== false && updatedServiceRecord?.veri) {
          const updatedData = updatedServiceRecord.veri.servisKaydi || updatedServiceRecord.veri;
          console.log('[handleCreateAppointment] Güncellenmiş servis kaydı verisi:', {
            id: updatedData?.id || updatedData?._id,
            durum: updatedData?.durum,
            eskiDurum: serviceRecord?.durum
          });
          setServiceRecord(updatedData);
        } else {
          console.warn('[handleCreateAppointment] Backend\'den gelen response başarısız veya veri yok:', updatedServiceRecord);
        }
      } catch (reloadError) {
        console.error('[handleCreateAppointment] Servis kaydı yeniden yüklenirken hata:', reloadError);
      }
      
      // Randevu durumu güncellendiğinde veya randevu oluşturulduğunda randevu listesini yeniden yükle
      try {
        await loadAppointments();
      } catch (reloadError) {
        console.error('Randevular yeniden yüklenirken hata:', reloadError);
      }
      
      if (window.showNotification) {
        window.showNotification('success', 'Randevu başarıyla oluşturuldu');
      }
      
      // Modal'ı kapat
      setIsNewAppointmentModalOpen(false);
      setAutoSelectedTechService(null);
      
      // Randevuları yeniden yükle (durum güncellemelerinden sonra)
      try {
        const appointmentsResponse = await fetchAppointmentsByServiceRecord(id, { aktif: true });
        console.log('Yeniden yüklenen randevular response:', appointmentsResponse);
        const list = appointmentsResponse?.veri?.randevular || appointmentsResponse?.veri?.randevular || appointmentsResponse?.veri || [];
        console.log('Yeniden yüklenen randevular listesi:', list);
        
        // Randevu durumlarını kontrol et
        list.forEach((apt, index) => {
          console.log(`Randevu ${index + 1} durumu:`, apt.durum);
        });
        
        setAppointments(Array.isArray(list) ? list : []);
        
        // Eğer liste boşsa ve yeni randevu varsa, onu ekle
        if (list.length === 0 && newAppointment) {
          console.log('Liste boş, yeni randevuyu ekliyorum:', newAppointment);
          setAppointments([newAppointment]);
        }
      } catch (reloadError) {
        console.error('Randevular yeniden yüklenirken hata:', reloadError);
        // Hata olsa bile yeni randevuyu ekle
        if (newAppointment) {
          setAppointments(prev => [...prev, newAppointment]);
        }
      }
    } catch (error) {
      console.error('Randevu oluşturma hatası:', error);
      setNewAppointmentError(getErrorMessage(error, 'Randevu oluşturulamadı'));
    } finally {
      setNewAppointmentSubmitting(false);
    }
  };

  // Minimum tarih (bugünden itibaren)
  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split('T')[0];
  };

  // Randevu teknik servis atama
  const handleAssignTechService = async (appointmentId, techServiceId) => {
    try {
      const updatePayload = {
        teknikServisId: techServiceId,
      };
      
      // Cihaz bilgilerinden garanti bitiş tarihini hesapla
      if (serviceRecord?.cihazlar && serviceRecord.cihazlar.length > 0) {
        const device = serviceRecord.cihazlar[0]; // İlk cihazı al
        if (device.garantiBaslangicTarihi && device.garantiSuresi) {
          updatePayload.garantiBaslangicTarihi = device.garantiBaslangicTarihi;
          updatePayload.garantiSuresi = device.garantiSuresi;
        }
      }
      
      const response = await updateAppointment(id, appointmentId, updatePayload);
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Teknik servis atanamadı');
      }
      
      if (window.showNotification) {
        window.showNotification('success', 'Teknik servis atandı');
      }
      
      // Randevuları yeniden yükle
      const appointmentsResponse = await fetchAppointmentsByServiceRecord(id, { aktif: true });
      const list = appointmentsResponse?.veri?.randevular || appointmentsResponse?.veri || [];
      setAppointments(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Teknik servis atama hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Teknik servis atanamadı'));
      }
    }
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Servis kaydı yükleniyor</div>
        </div>
      </main>
    );
  }

  if (error || !serviceRecord) {
    return (
      <main className="main-content">
        <div className="error-state">{safeText(error || 'Servis kaydı bulunamadı')}</div>
      </main>
    );
  }

  const customer = serviceRecord.musteri || {};
  const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim();
  const customerPhone = customer.telefon || customer.phone || customer.gsm || '---';
  const priorityToken = getPriorityToken(serviceRecord.onemDerecesi);
  const onemBorderClass = getOnemBorderClass(serviceRecord.onemDerecesi);
  const onemRowClass = getOnemRowClass(serviceRecord.onemDerecesi);
  const selectedDevices =
    (Array.isArray(serviceRecord.cihazlar) && serviceRecord.cihazlar) ||
    (Array.isArray(serviceRecord.cihazKayitlari) && serviceRecord.cihazKayitlari) ||
    (Array.isArray(serviceRecord.cihazBilgileri) && serviceRecord.cihazBilgileri) ||
    [];
  const deviceNotSelected = Boolean(serviceRecord.cihazSecilmedi);
  const deviceNote = serviceRecord.cihazAciklama || serviceRecord.cihazNotu || '';
  // Seçili Arızalar: arizaKayitlari, arizalar veya populate edilmiş arizaIds'den al
  const arizaList = (() => {
    if (Array.isArray(serviceRecord.arizaKayitlari) && serviceRecord.arizaKayitlari.length > 0) {
      return serviceRecord.arizaKayitlari;
    }
    if (Array.isArray(serviceRecord.arizalar) && serviceRecord.arizalar.length > 0) {
      return serviceRecord.arizalar;
    }
    const arizaIds = serviceRecord.arizaIds;
    if (Array.isArray(arizaIds) && arizaIds.length > 0) {
      return arizaIds.map(a => {
        const obj = a && typeof a === 'object' ? a : { _id: a };
        const id = obj._id ? (typeof obj._id === 'object' && obj._id.toString ? obj._id.toString() : String(obj._id)) : String(a);
        const ad = obj.icerikAdi || obj.arizaAdi || obj.urunAdi || obj.ad || '';
        return { id, _id: id, icerikAdi: ad, arizaAdi: ad, ad };
      });
    }
    return [];
  })();
  const arizaNote = serviceRecord.arizaNotu || '';
  const isArizaKaydi = String(serviceRecord.islemTipi || '') === 'Arıza';
  const isSantralKaydi = Boolean(
    serviceRecord.gorusmeKaydiId
    || serviceRecord.gorusmeKaydi?._id
    || serviceRecord.gorusmeKaydi?.id
    || serviceRecord.gorusmeKaydi?.tarihSaat
  );
  const mevcutTeknikServis = serviceRecord.aktifAtamaTeknikServis?.ad
    ? `${serviceRecord.aktifAtamaTeknikServis.ad}${serviceRecord.aktifAtamaTeknikServis.kod != null && serviceRecord.aktifAtamaTeknikServis.kod !== ''
      ? ` (kod: ${serviceRecord.aktifAtamaTeknikServis.kod})`
      : ''}`
    : '—';
  const mevcutTeknisyen = serviceRecord.aktifAtamaTeknisyen?.adSoyad
    || [serviceRecord.aktifAtamaTeknisyen?.adi, serviceRecord.aktifAtamaTeknisyen?.soyadi].filter(Boolean).join(' ')
    || serviceRecord.sonRandevuTeknisyen?.adSoyad
    || [serviceRecord.sonRandevuTeknisyen?.adi, serviceRecord.sonRandevuTeknisyen?.soyadi].filter(Boolean).join(' ')
    || '—';
  const address = serviceRecord.adres || {};
  const districtName = address.district?.name || address.district?.ad || '---';
  const cityName = address.city?.name || address.city?.ad || '---';
  const isKayitTamamlandi = String(serviceRecord.durum || '').trim() === 'Tamamlandı';
  const showKayitDuzenleActions =
    !isTech
    && (isSales || isAdminOrMod)
    && serviceRecord.durum !== 'Taslak'
    && !isKayitTamamlandi;
  const salesKayitSaltOkunur = isSales && isKayitTamamlandi;
  const musteriId =
    customer.id || customer._id || serviceRecord.musteriId?.id || serviceRecord.musteriId?._id
    || serviceRecord.musteriId;
  const initialCihazIds = (serviceRecord.cihazIds || selectedDevices || []).map((c) =>
    (typeof c === 'object' ? c.id || c._id : c),
  ).filter(Boolean);

  const statusToken = (() => {
    const value = String(serviceRecord.durum || '').toLowerCase();
    if (value.includes('sipariş') || value.includes('siparis')) return 'siparis';
    if (value.includes('devam')) return 'progress';
    if (value.includes('tamam') || value.includes('bitti')) return 'done';
    if (value.includes('iptal')) return 'cancel';
    return 'new';
  })();

  return (
    <main className="main-content service-record-update">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Servis Kaydı Yönetimi</h1>
        </div>
      </div>

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'record' ? 'active' : ''}`}
          onClick={() => setActiveTab('record')}
        >
          Kayıt Bilgileri
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          Randevu
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'siparisler' ? 'active' : ''}`}
          onClick={() => setActiveTab('siparisler')}
        >
          Siparişler
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'iade-kargolari' ? 'active' : ''}`}
          onClick={() => setActiveTab('iade-kargolari')}
        >
          İade Kargoları
        </button>
      </div>

      {salesKayitSaltOkunur && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            borderRadius: 8,
            background: '#fef3c7',
            color: '#92400e',
            fontSize: 14,
          }}
        >
          Tamamlanan servis kaydı güncellenemez.
        </div>
      )}

      {activeTab === 'record' && (
        <div className="service-record-info service-record-info--cards">
          <div className={`kayit-bilgi-card${onemBorderClass ? ` ${onemBorderClass}` : ''}`}>
            <div className="kayit-bilgi-card__header">
              <i className="fas fa-clipboard-list"></i>
              <h3>Kayıt Özeti</h3>
              <span className={`kayit-kaynak-badge ${isSantralKaydi ? 'kayit-kaynak-badge--santral' : 'kayit-kaynak-badge--manuel'}`}>
                <i className={`fas ${isSantralKaydi ? 'fa-phone-alt' : 'fa-keyboard'}`}></i>
                {isSantralKaydi ? 'Santral çağrısı' : 'Manuel'}
              </span>
            </div>
            <div className="kayit-bilgi-card__body">
              <div className="kayit-bilgi-grid">
                <div className="kayit-bilgi-row">
                  <i className="fas fa-user"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Müşteri</span>
                    <span className="kayit-bilgi-row__value">{customerName || '—'}</span>
                    <span className="kayit-bilgi-row__meta">{customerPhone}</span>
                  </div>
                </div>
                <div className="kayit-bilgi-row">
                  <i className="fas fa-tools"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">İşlem Tipi</span>
                    <span className="kayit-bilgi-row__value">{serviceRecord.islemTipi || '—'}</span>
                  </div>
                </div>
                <div className="kayit-bilgi-row">
                  <i className="fas fa-exclamation-circle"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Önem Derecesi</span>
                    <span className={`priority-badge priority-${priorityToken}`}>
                      {serviceRecord.onemDerecesi || '—'}
                    </span>
                  </div>
                </div>
                <div className="kayit-bilgi-row">
                  <i className="fas fa-info-circle"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Durum</span>
                    <span className={`status-chip status-${statusToken}`}>
                      {serviceRecord.durum || '—'}
                    </span>
                  </div>
                </div>
                {serviceRecord.odemeBeyani === true && (
                  <div className="kayit-bilgi-row">
                    <i className="fas fa-money-bill-wave"></i>
                    <div className="kayit-bilgi-row__content">
                      <span className="kayit-bilgi-row__label">Ödeme Beyanı</span>
                      <span className="kayit-bilgi-row__value">
                        Müşteri ödeme beyanı ile açıldı
                        {serviceRecord.odemeBeyanSonucu
                          ? ` — ${serviceRecord.odemeBeyanSonucu === 'alindi' ? 'Ödeme alındı' : 'Ödeme alınamadı'}`
                          : ''}
                      </span>
                    </div>
                  </div>
                )}
                {(serviceRecord.siparisNumarasi || serviceRecord.siparisId) && (
                  <div className="kayit-bilgi-row">
                    <i className="fas fa-shopping-cart"></i>
                    <div className="kayit-bilgi-row__content">
                      <span className="kayit-bilgi-row__label">Bağlı Sipariş</span>
                      <span className="kayit-bilgi-row__value kayit-bilgi-row__value--inline">
                        {serviceRecord.siparisNumarasi || '—'}
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          style={{ fontSize: 12, padding: '4px 8px' }}
                          onClick={async () => {
                            const sid = serviceRecord.siparisId?.toString?.() || serviceRecord.siparisId;
                            if (!sid) return;
                            try {
                              const res = await fetchSiparisDetay(sid);
                              if (res?.basarili && res?.veri) setLinkedSiparisModal(res.veri);
                            } catch {
                              if (window.showNotification) window.showNotification('error', 'Sipariş detayı yüklenemedi');
                            }
                          }}
                        >
                          Detay
                        </button>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {serviceRecord.islemTipi === 'Kurulum' && (
            <>
              <div className="kayit-bilgi-card">
                <div className="kayit-bilgi-card__header">
                  <i className="fas fa-box"></i>
                  <h3>Beyan Edilen Ürün Bilgileri</h3>
                </div>
                <div className="kayit-bilgi-card__body">
                  <div className="kayit-bilgi-kv">
                    <div className="kayit-bilgi-kv__satir">
                      <span className="kayit-bilgi-kv__etiket">Ürün adı</span>
                      <span className="kayit-bilgi-kv__deger">{serviceRecord.kurulumUrunAdi || '—'}</span>
                    </div>
                    <div className="kayit-bilgi-kv__satir">
                      <span className="kayit-bilgi-kv__etiket">Ürün kodu</span>
                      <span className="kayit-bilgi-kv__deger">{serviceRecord.kurulumUrunKodu || '—'}</span>
                    </div>
                    <div className="kayit-bilgi-kv__satir">
                      <span className="kayit-bilgi-kv__etiket">Seri No</span>
                      <span className="kayit-bilgi-kv__deger">{serviceRecord.kurulumSeriNo || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
              {serviceRecord.checklistTelefonCevaplari?.cevaplar?.length > 0 && (
                <div className="kayit-bilgi-card">
                  <div className="kayit-bilgi-card__header">
                    <i className="fas fa-clipboard-check"></i>
                    <h3>Kontrol Listesi (Telefon / MH)</h3>
                  </div>
                  <div className="kayit-bilgi-card__body">
                    <div className="kurulum-gereksinimlar__liste">
                      {serviceRecord.checklistTelefonCevaplari.cevaplar.map((g, idx) => (
                        <div key={idx} className="kurulum-gereksinim-item">
                          <span className="kurulum-gereksinim-item__soru">{g.soruMetni || '—'}</span>
                          <span className={`kurulum-gereksinim-item__badge kurulum-gereksinim-item__badge--${g.cevap === 'hayir' ? 'hayir' : 'evet'}`}>
                            {g.cevap === 'hayir'
                              ? 'Hayır'
                              : g.deger?.trim()
                                ? `Evet — ${g.deger.trim()}`
                                : 'Evet'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="kayit-bilgi-card">
            <div className="kayit-bilgi-card__header">
              <i className="fas fa-map-marker-alt"></i>
              <h3>Adres</h3>
            </div>
            <div className="kayit-bilgi-card__body">
              <div className="kayit-bilgi-row">
                <i className="fas fa-home"></i>
                <div className="kayit-bilgi-row__content">
                  <span className="kayit-bilgi-row__value">{address.adres || '—'}</span>
                  <span className="kayit-bilgi-row__meta">{districtName} / {cityName}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="kayit-bilgi-card">
            <div className="kayit-bilgi-card__header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className="fas fa-microchip"></i>
              <h3 style={{ flex: 1, margin: 0 }}>Seçilen Cihazlar</h3>
              {showKayitDuzenleActions && serviceRecord.islemTipi !== 'Kurulum' && (
                <button
                  type="button"
                  className="secondary-btn"
                  style={{ fontSize: 13, padding: '6px 12px' }}
                  onClick={() => setCihazDuzenleOpen(true)}
                >
                  Cihazı düzenle
                </button>
              )}
            </div>
            <div className="kayit-bilgi-card__body">
              {deviceNotSelected ? (
                <div className="kayit-bilgi-empty">Cihaz seçilmedi</div>
              ) : selectedDevices.length > 0 ? (
                <div className="cihaz-kartlari">
                  {selectedDevices.map((device, index) => (
                    <div key={device.id || device._id || device.seriNumarasi || device.seriNo || index} className="kayit-bilgi-kv kayit-bilgi-kv--inset">
                      <div className="kayit-bilgi-kv__satir">
                        <span className="kayit-bilgi-kv__etiket">Cihaz adı</span>
                        <span className="kayit-bilgi-kv__deger">{getDeviceName(device)}</span>
                      </div>
                      <div className="kayit-bilgi-kv__satir">
                        <span className="kayit-bilgi-kv__etiket">Seri No</span>
                        <span className="kayit-bilgi-kv__deger">{getDeviceSerial(device)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="kayit-bilgi-empty">—</div>
              )}
              <div className="kayit-bilgi-row" style={{ marginTop: selectedDevices.length || deviceNotSelected ? 12 : 0 }}>
                <i className="fas fa-sticky-note"></i>
                <div className="kayit-bilgi-row__content">
                  <span className="kayit-bilgi-row__label">Cihaz Notu</span>
                  <span className="kayit-bilgi-row__value">{deviceNote || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {isArizaKaydi && (
            <div className="kayit-bilgi-card">
              <div className="kayit-bilgi-card__header">
                <i className="fas fa-exclamation-triangle"></i>
                <h3>Arıza Bilgileri</h3>
              </div>
              <div className="kayit-bilgi-card__body">
                <div className="kayit-bilgi-row">
                  <i className="fas fa-list"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Seçili Arızalar</span>
                    {arizaList.length > 0 ? (
                      <div className="info-section__tags">
                        {arizaList.map((ariza) => (
                          <span key={ariza.id || ariza._id} className="tag tag--danger">
                            {ariza.icerikAdi || ariza.arizaAdi || ariza.ad || '—'}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="kayit-bilgi-row__value">—</span>
                    )}
                  </div>
                </div>
                <div className="kayit-bilgi-row">
                  <i className="fas fa-comment-alt"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Arıza Notu</span>
                    <span className="kayit-bilgi-row__value">{arizaNote || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="kayit-bilgi-card">
            <div className="kayit-bilgi-card__header">
              <i className="fas fa-comment-dots"></i>
              <h3>Servis Kaydı notu</h3>
            </div>
            <div className="kayit-bilgi-card__body">
              <div className="kayit-bilgi-row">
                <i className="fas fa-sticky-note"></i>
                <div className="kayit-bilgi-row__content">
                  <span className="kayit-bilgi-row__value kayit-bilgi-row__value--pre">
                    {serviceRecord.notlar || '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {serviceRecord.gorusmeKaydi?.tarihSaat && (
            <div className="kayit-bilgi-card">
              <div className="kayit-bilgi-card__header">
                <i className="fas fa-phone"></i>
                <h3>Oluşturulduğu Görüşme Kaydı</h3>
              </div>
              <div className="kayit-bilgi-card__body">
                <div className="kayit-bilgi-row">
                  <i className="fas fa-clock"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__value">{serviceRecord.gorusmeKaydi.tarihSaat}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="kayit-bilgi-card">
            <div className="kayit-bilgi-card__header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className="fas fa-building"></i>
              <h3 style={{ flex: 1, margin: 0 }}>Mevcut Atama</h3>
              {showKayitDuzenleActions && serviceRecord.aktifAtamaTeknikServis?.ad && (
                <button
                  type="button"
                  className="secondary-btn"
                  style={{ fontSize: 13, padding: '6px 12px' }}
                  onClick={() => setTsDegistirOpen(true)}
                >
                  Teknik servisi değiştir
                </button>
              )}
            </div>
            <div className="kayit-bilgi-card__body">
              <div className="kayit-bilgi-grid">
                <div className="kayit-bilgi-row">
                  <i className="fas fa-building"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Mevcut teknik servis</span>
                    <span className="kayit-bilgi-row__value">{mevcutTeknikServis}</span>
                  </div>
                </div>
                <div className="kayit-bilgi-row">
                  <i className="fas fa-user-cog"></i>
                  <div className="kayit-bilgi-row__content">
                    <span className="kayit-bilgi-row__label">Mevcut teknisyen</span>
                    <span className="kayit-bilgi-row__value">{mevcutTeknisyen}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <TechServiceAssignmentHistory
            history={serviceRecord.teknikServisAtamaGecmisi}
            serviceRecord={serviceRecord}
          />
        </div>
      )}

      {activeTab === 'appointments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-dark)', margin: 0 }}>Randevular</h2>
            {user?.rol === 'tech-service' && (
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsNewAppointmentModalOpen(true)}
                disabled={serviceRecord?.durum !== SERVICE_RECORD_STATUSES.RANDEVU_BEKLENIYOR && serviceRecord?.durum !== 'Randevu Bekliyor'}
                style={{
                  opacity: (serviceRecord?.durum !== SERVICE_RECORD_STATUSES.RANDEVU_BEKLENIYOR && serviceRecord?.durum !== 'Randevu Bekliyor') ? 0.5 : 1,
                  cursor: (serviceRecord?.durum !== SERVICE_RECORD_STATUSES.RANDEVU_BEKLENIYOR && serviceRecord?.durum !== 'Randevu Bekliyor') ? 'not-allowed' : 'pointer'
                }}
                title={(serviceRecord?.durum !== SERVICE_RECORD_STATUSES.RANDEVU_BEKLENIYOR && serviceRecord?.durum !== 'Randevu Bekliyor') ? `Yeni randevu oluşturmak için servis kaydı durumu "${SERVICE_RECORD_STATUSES.RANDEVU_BEKLENIYOR}" olmalıdır` : 'Yeni Randevu Oluştur'}
              >
                <i className="fas fa-plus"></i>
                <span>Yeni Randevu</span>
              </button>
            )}
          </div>

          {appointmentsLoading ? (
            <div className="loading-state" style={{ padding: '40px 20px' }}>
              <div className="loading-spinner"></div>
              <div className="loading-text">Randevular yükleniyor</div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
              <i className="fas fa-calendar-times" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
              <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Randevu bulunamadı</div>
            </div>
          ) : (
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Saat Aralığı</th>
                    <th>Teknik Servis</th>
                    <th>Teknisyen</th>
                    <th>Durum</th>
                    <th className="islemler-sutunu">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => {
                    const appointmentId = appointment.id || appointment._id;
                    const appointmentDate = appointment.randevuTarihi 
                      ? new Date(appointment.randevuTarihi).toLocaleDateString('tr-TR')
                      : '—';
                    const technicianName = appointment.teknisyen 
                      ? `${appointment.teknisyen.adi || ''} ${appointment.teknisyen.soyadi || ''}`.trim() || '—'
                      : '—';
                    const techServiceName = appointment.teknikServis?.ad || appointment.teknikServis?.name || '—';
                    
                    return (
                      <tr key={appointmentId} className={onemRowClass || undefined}>
                        <td>{appointmentDate}</td>
                        <td>{appointment.randevuSaati || appointment.randevuSaatAraligi || '—'}</td>
                        <td>{techServiceName}</td>
                        <td>{technicianName}</td>
                        <td>
                          {(() => {
                            const displayDurum = (appointment.iptalBilgileri || appointment.iptalNedeni)
                              ? APPOINTMENT_STATUSES.RANDEVU_IPTAL_EDILDI
                              : (appointment.durum || 'Beklemede');
                            const isIptal = displayDurum === 'Randevu İptal Edildi' || displayDurum === 'İptal';
                            const isTamamlandi = displayDurum === 'Randevu Tamamlandı' || displayDurum === 'Onaylandı';
                            return (
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background: isTamamlandi ? '#d1fae5' : isIptal ? '#fee2e2' : '#fef3c7',
                                color: isTamamlandi ? '#065f46' : isIptal ? '#991b1b' : '#92400e'
                              }}>
                                {displayDurum}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="table-actions islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => {
                              // Tech-service ve tech-person /appointments/:id sayfasına gitsin (TechServiceRoute)
                              const role = (user?.rol || '').toLowerCase();
                              if (role === 'tech-service' || role === 'tech-person') {
                                navigateWithFrom(navigate, `/appointments/${appointmentId}`, location);
                              } else {
                                navigateWithFrom(navigate, `/service-records/${id}/appointments/${appointmentId}`, location);
                              }
                            }}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          {!salesKayitSaltOkunur && appointment.durum !== 'Randevu İptal Edildi' && appointment.durum !== 'İptal' && !appointment.iptalBilgileri && !appointment.iptalNedeni && (
                            <button
                              type="button"
                              className="category-action-btn delete-btn"
                              onClick={async () => {
                                const confirmMessage =
                                  'Bu randevuyu iptal etmek istediğinize emin misiniz?\n\n' +
                                  '⚠️ UYARI: Bu işlem geri alınamaz!\n\n' +
                                  'Randevu iptal edildiğinde:\n' +
                                  '• Tüm atamalar (teknisyen, işlemler, parçalar) silinecek\n' +
                                  '• Garanti ve fatura bilgileri temizlenecek\n' +
                                  '• Servis kaydı "Randevu Bekleniyor" durumuna dönecek';

                                const confirmedCancel = await confirm({
                                  title: 'Randevu iptali',
                                  message: confirmMessage,
                                  variant: 'danger',
                                  confirmLabel: 'Evet, iptal et',
                                });
                                if (!confirmedCancel) return;

                                const iptalNedeni = await promptText({
                                  title: 'İptal nedeni',
                                  message: 'İptal nedeni en az 10 karakter olmalıdır.',
                                  placeholder: 'İptal nedeni',
                                  minLength: 10,
                                  multiline: true,
                                });

                                if (iptalNedeni === null) return;

                                try {
                                    // Randevuyu iptal et (backend zaten servis kaydı durumunu güncelliyor)
                                    await cancelAppointment(id, appointmentId, iptalNedeni, user?.rol);
                                    
                                    if (window.showNotification) {
                                      window.showNotification('success', 'Randevu iptal edildi. Servis kaydı durumu "Randevu Bekleniyor" olarak güncellendi.');
                                    }
                                    
                                    // Randevuları ve servis kaydını yeniden yükle
                                    await loadAppointments();
                                    const serviceRecordResponse = await fetchServiceRecordDetail(id);
                                    if (serviceRecordResponse?.basarili && serviceRecordResponse?.veri) {
                                      const updatedData = serviceRecordResponse.veri.servisKaydi || serviceRecordResponse.veri;
                                      setServiceRecord(updatedData);
                                    }
                                  } catch (error) {
                                    if (window.showNotification) {
                                      window.showNotification('error', getErrorMessage(error, 'Randevu iptal edilemedi'));
                                    }
                                  }
                              }}
                              title="Randevu İptal Et"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Siparişler Tab - Düz liste (servis kaydına göre) */}
      {activeTab === 'siparisler' && (
        <div className="service-record-info" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 16 }}>Siparişler</h2>
          {siparislerLoading ? (
            <div className="loading-state" style={{ padding: '40px 20px' }}>
              <div className="loading-spinner"></div>
              <div className="loading-text">Siparişler yükleniyor</div>
            </div>
          ) : siparisler.length === 0 ? (
            <div className="empty-state" style={{ padding: 40, textAlign: 'center', color: 'var(--text-gray)' }}>
              Bu servis kaydına ait sipariş bulunamadı
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {siparisler.map((siparis) => (
                <div key={siparis.id} style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 16, background: '#fff' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-dark)' }}>
                    {siparis.siparisNumarasi} • {siparis.durum || '—'}
                  </div>
                  {(siparis.pozisyonlar || []).length > 0 ? (
                    <table className="admins-table" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Parça Adı</th>
                          <th>Parça Kodu</th>
                          <th style={{ textAlign: 'center' }}>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {siparis.pozisyonlar.map((p, idx) => (
                          <tr key={idx}>
                            <td>{p.parcaAdi || '—'}</td>
                            <td>{p.parcaKodu || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{p.adet ?? 1}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--text-gray)' }}>İçerik bilgisi yok</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* İade Kargoları Tab - Düz liste (servis kaydına göre) */}
      {activeTab === 'iade-kargolari' && (
        <div className="service-record-info" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 16 }}>İade Kargoları</h2>
          {iadeKargolariLoading ? (
            <div className="loading-state" style={{ padding: '40px 20px' }}>
              <div className="loading-spinner"></div>
              <div className="loading-text">İade kargoları yükleniyor</div>
            </div>
          ) : iadeKargolari.length === 0 ? (
            <div className="empty-state" style={{ padding: 40, textAlign: 'center', color: 'var(--text-gray)' }}>
              Bu servis kaydına ait iade kargo bulunamadı
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {iadeKargolari.map((ik) => {
                const parcalar = ik.parcalar || [];
                const kullanilmamisaparcalar = ik.kullanilmamisaparcalar || [];
                const hasParcalar = parcalar.length > 0;
                const hasKullanilmamis = kullanilmamisaparcalar.length > 0;
                return (
                  <div key={ik.id} style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 16, background: '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-dark)' }}>
                      {ik.iadeKargoNumarasi} • {IADE_KARGO_DURUM_LABEL[ik.durum] || ik.durum || '—'}
                    </div>
                    {hasParcalar && (
                      <div style={{ marginBottom: hasKullanilmamis ? 16 : 0 }}>
                        <FieldLabel
                          as="div"
                          style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: 'var(--text-gray)', marginBottom: 6 }}
                        >
                          İade Edilen Parçalar
                        </FieldLabel>
                        <table className="admins-table" style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Parça Adı</th>
                              <th>Parça Kodu</th>
                              <th style={{ textAlign: 'center' }}>Adet</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parcalar.map((p, idx) => (
                              <tr key={`p-${idx}`}>
                                <td>{p.parcaAdi || '—'}</td>
                                <td>{p.parcaKodu || '—'}</td>
                                <td style={{ textAlign: 'center' }}>{p.adet ?? 1}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {hasKullanilmamis && (
                      <div>
                        <FieldLabel
                          as="div"
                          style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: 'var(--text-gray)', marginBottom: 6 }}
                        >
                          Kullanılmamış Parçalar
                        </FieldLabel>
                        <table className="admins-table" style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Parça Adı</th>
                              <th>Parça Kodu</th>
                              <th style={{ textAlign: 'center' }}>Adet</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kullanilmamisaparcalar.map((p, idx) => (
                              <tr key={`k-${idx}`}>
                                <td>{p.parcaAdi || '—'}</td>
                                <td>{p.parcaKodu || '—'}</td>
                                <td style={{ textAlign: 'center' }}>{p.adet ?? 1}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!hasParcalar && !hasKullanilmamis && (
                      <div style={{ fontSize: 13, color: 'var(--text-gray)' }}>Parça bilgisi yok</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Yeni Randevu Oluşturma Modal - Wizard */}
      <AppointmentWizardModal
        isOpen={isNewAppointmentModalOpen}
        onClose={() => setIsNewAppointmentModalOpen(false)}
        onSubmit={handleCreateAppointment}
        serviceRecordId={id}
        autoSelectedTechService={autoSelectedTechService}
        submitting={newAppointmentSubmitting}
        error={newAppointmentError}
      />

      {linkedSiparisModal && (
        <SiparisDetayModal
          siparis={linkedSiparisModal}
          onClose={() => setLinkedSiparisModal(null)}
          onRefresh={() => {
            if (!id) return;
            fetchServiceRecordDetail(id).then((r) => {
              const rec = r?.veri?.servisKaydi || r?.veri;
              if (rec) setServiceRecord(rec);
            });
            fetchSiparisler({ servisKaydiId: id, limit: 100 }).then((r) => setSiparisler(r?.veri?.siparisler || []));
          }}
          isAdmin={user?.rol !== 'tech-service'}
        />
      )}

      {/* Eski modal (yorum satırı olarak saklanıyor - backend hazır olmadan test için) */}
      {false && isNewAppointmentModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsNewAppointmentModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Yeni Randevu Oluştur (ESKİ)</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsNewAppointmentModalOpen(false)}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="modal-body" onSubmit={(e) => {
              e.preventDefault();
              handleCreateAppointment({
                randevuTarihi: newAppointmentForm.randevuTarihi,
                randevuSaati: newAppointmentForm.randevuSaatAraligi?.split('-')[0],
                teknikServisId: newAppointmentForm.teknikServisId || autoSelectedTechService?._id,
                aciklama: newAppointmentForm.aciklama
              });
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label className="form-field">
                  <span>Randevu Tarihi *</span>
                  <input
                    type="date"
                    value={newAppointmentForm.randevuTarihi}
                    onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, randevuTarihi: e.target.value })}
                    min={getMinDate()}
                    required
                  />
                </label>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>Saat Aralığı *</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '12px',
                }}>
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      className={newAppointmentForm.randevuSaatAraligi === slot.value ? 'primary-btn active' : 'secondary-btn'}
                      onClick={() => setNewAppointmentForm({ ...newAppointmentForm, randevuSaatAraligi: slot.value })}
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        background: newAppointmentForm.randevuSaatAraligi === slot.value ? 'var(--primary-blue)' : '#ffffff',
                        color: newAppointmentForm.randevuSaatAraligi === slot.value ? '#ffffff' : 'var(--text-dark)',
                        border: `2px solid ${newAppointmentForm.randevuSaatAraligi === slot.value ? 'var(--primary-blue)' : 'var(--border-color)'}`,
                      }}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label className="form-field">
                  <span>Teknik Servis *</span>
                  
                  {/* Otomatik seçilen teknik servis gösterimi */}
                  {autoSelectedTechService && !showTechServiceSelection && (
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        background: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '8px',
                        marginBottom: '12px'
                      }}
                    >
                      <i className="fas fa-check-circle" style={{ color: '#0284c7' }}></i>
                      <span style={{ flex: 1, fontSize: '14px', color: 'var(--text-dark)' }}>
                        {autoSelectedTechService.ad || autoSelectedTechService.name || '—'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowTechServiceSelection(true);
                          setAutoSelectedTechService(null);
                          setNewAppointmentForm(prev => ({ ...prev, teknikServisId: '' }));
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Seçimi kaldır"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                  
                  {/* Yetkili servis bulunamadı mesajı */}
                  {!autoSelectedTechService && !showTechServiceSelection && techServices.length === 0 && techServiceCityId && (
                    <div style={{
                      padding: '12px',
                      textAlign: 'center',
                      color: '#ef4444',
                      fontSize: '14px',
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                      Yetkili servis bulunamadı
                    </div>
                  )}
                  
                  {/* Manuel teknik servis seçimi */}
                  {showTechServiceSelection && (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <div className="form-grid">
                          <label className="form-field">
                            <span>İl</span>
                            <select
                              value={techServiceCityId}
                              onChange={(e) => {
                                setTechServiceCityId(e.target.value);
                                setTechServiceDistrictId('');
                                setTechServices([]);
                              }}
                            >
                              <option value="">İl Seçiniz</option>
                              {iller.map((il) => (
                                <option key={il.id} value={String(il.id)}>
                                  {il.name || il.ad}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="form-field">
                            <span>İlçe</span>
                            <select
                              value={techServiceDistrictId}
                              onChange={(e) => {
                                setTechServiceDistrictId(e.target.value);
                              }}
                              disabled={!techServiceCityId}
                            >
                              <option value="">İlçe Seçiniz (Opsiyonel)</option>
                              {techServiceIlceler.map((ilce) => {
                                // API formatı: { id: number, adi: string, cityId: number }
                                const ilceName = ilce.adi || ilce.ad || ilce.name || '';
                                return (
                                  <option key={ilce.id} value={String(ilce.id)}>
                                    {ilceName}
                                  </option>
                                );
                              })}
                            </select>
                          </label>
                        </div>
                      </div>
                      
                      {techServicesLoading ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-gray)' }}>
                          Teknik servisler yükleniyor...
                        </div>
                      ) : techServices.length > 0 ? (
                        <label className="form-field" style={{ marginBottom: 0 }}>
                          <span>Teknik Servis *</span>
                          <select
                            value={newAppointmentForm.teknikServisId}
                            onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, teknikServisId: e.target.value })}
                            style={{ width: '100%' }}
                            required
                          >
                            <option value="">Teknik Servis Seçiniz</option>
                            {techServices.map((service) => (
                              <option key={service.id || service._id} value={service.id || service._id}>
                                {service.ad || service.name || '—'}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : techServiceCityId ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-gray)', fontSize: '14px' }}>
                          Bu bölgede teknik servis bulunamadı
                        </div>
                      ) : (
                        <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-gray)', fontSize: '14px' }}>
                          Teknik servis seçmek için il seçiniz
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Otomatik seçim yoksa ve manuel seçim de kapalıysa buton göster */}
                  {!autoSelectedTechService && !showTechServiceSelection && (
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => setShowTechServiceSelection(true)}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      <i className="fas fa-search" style={{ marginRight: '8px' }}></i>
                      Teknik Servis Seç
                    </button>
                  )}
                </label>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label className="form-field">
                  <span>Açıklama</span>
                  <textarea
                    value={newAppointmentForm.aciklama}
                    onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, aciklama: e.target.value })}
                    rows="3"
                    maxLength={500}
                    placeholder="Randevu ile ilgili açıklama..."
                  />
                </label>
              </div>
              
              {newAppointmentError && (
                <div className="error-state small" style={{ marginBottom: '16px' }}>
                  {safeText(newAppointmentError)}
                </div>
              )}
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setIsNewAppointmentModalOpen(false)}
                  disabled={newAppointmentSubmitting}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={newAppointmentSubmitting}
                >
                  {newAppointmentSubmitting ? 'Oluşturuluyor...' : 'Randevu Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <TeknikServisDegistirModal
        isOpen={tsDegistirOpen}
        recordId={id}
        customerName={customerName || '—'}
        currentTsId={serviceRecord.aktifAtamaTeknikServis?.id || serviceRecord.aktifAtamaTeknikServis?._id}
        currentTsAd={serviceRecord.aktifAtamaTeknikServis?.ad || '—'}
        onClose={() => setTsDegistirOpen(false)}
        onSuccess={reloadServiceRecord}
      />

      <ServisKaydiCihazDuzenleModal
        isOpen={cihazDuzenleOpen}
        servisKaydiId={id}
        musteriId={musteriId}
        islemTipi={serviceRecord.islemTipi}
        initialCihazIds={initialCihazIds}
        initialCihazSecilmedi={deviceNotSelected}
        initialCihazAciklama={deviceNote}
        onClose={() => setCihazDuzenleOpen(false)}
        onSuccess={reloadServiceRecord}
      />
    </main>
  );
};

export default ServiceRecordUpdatePage;


